import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Container,
  Dialog,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  IconButton,
  TextField as MuiTextField,
  Box,
  Alert,
  MenuItem,
  FormControlLabel,
  Chip,
  Card,
  CardContent,
  CardHeader,
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm, useWatch } from 'react-hook-form';
import { FormProvider, Table, TextField, SelectTag, Checkbox } from '../../../../../components';
import { dropdownFetch, getKeyPathValue, request, toast } from '../../../../../utils';
import { REFERRAL_TYPE, TEST_FOR, FONT_TYPE } from '../../../../../constants';
import { LisTestItemMasterHelpForm } from './LisTestItemMasterHelpForm';
import { LisTestItemRefrenceRangeForm } from './LisTestItemRefrenceRangeForm';
import { LisTestItemRefrenceTextRangeForm } from './LisTestItemRefrenceTextRangeForm ';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import ScienceIcon from '@mui/icons-material/Science';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import axios from 'axios';
import { BASE_URL } from '../../../../../utils';

// CKEditor imports
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const TEST_ITEM_HEADER = [
  {
    header: 'Test Name ',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black',
            fontSize: '1.1rem',
            fontWeight: 600
          }}
        >
          {`${row.test_name}`}
        </span>
      );
    }
  },
  {
    header: 'Short Name',
    accessor: 'shortName',
    sx: { fontSize: '1.1rem', fontWeight: 500 }
  },
  {
    header: 'Method',
    accessor: 'method',
    sx: { fontSize: '1.1rem', fontWeight: 500 }
  },
  {
    header: 'Category',
    accessor: (row) => {
      return row.category?.category_name || '';
    },
    sx: { fontSize: '1.1rem', fontWeight: 500 }
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: {
      textTransform: 'uppercase',
      fontSize: '1.1rem',
      fontWeight: 600,
      color: 'primary.main'
    }
  }
];

const PARAMETER_HEADER = [
  {
    header: 'Parameter Name',
    accessor: 'name',
    sx: { fontSize: '1.1rem', fontWeight: 600 }
  },
  {
    header: 'Unit',
    accessor: 'unit',
    sx: { fontSize: '1.1rem', fontWeight: 500 }
  },
  {
    header: 'Input Type',
    accessor: 'inputType',
    sx: { fontSize: '1.1rem', fontWeight: 500 }
  },
  {
    header: 'Default Result',
    accessor: 'defaultResult',
    sx: { fontSize: '1.1rem', fontWeight: 500 }
  }
];

const DESCRIPTION_HEADER = [
  {
    header: 'Description',
    accessor: 'helpText',
    sx: {
      textTransform: 'capitalize',
      fontSize: '1.1rem',
      fontWeight: 500
    }
  }
];

// Input type options for dropdown
const INPUT_TYPE_OPTIONS = [
  { _id: 'numeric', name: 'Numeric' },
  { _id: 'text', name: 'Text' },
  { _id: 'paragraph', name: 'Paragraph' },
  { _id: 'calculated', name: 'Calculated' }
];

const initialtestitemDataObj = {
  data: [],
  loading: false
};

// Enhanced styles
const styles = {
  mainCard: {
    borderRadius: 3,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e0e0',
    overflow: 'hidden'
  },
  sectionCard: {
    borderRadius: 2,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    border: '1px solid #f0f0f0',
    marginBottom: 3
  },
  parameterCard: {
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e8e8e8',
    marginBottom: 2,
    backgroundColor: '#fafafa',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      backgroundColor: '#ffffff'
    }
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: '12px 12px 0 0',
    padding: '16px 24px'
  },
  actionButton: {
    borderRadius: 2,
    padding: '8px 20px',
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
    }
  },
  formLabel: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: '#2c3e50',
    marginBottom: 1
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      fontSize: '1.2rem',
      '& input': {
        fontSize: '1.2rem',
        padding: '12px 14px'
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: '1.2rem'
    }
  },
  selectField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      fontSize: '1.2rem'
    },
    '& .MuiInputLabel-root': {
      fontSize: '1.2rem'
    }
  },
  chip: {
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: 2
  }
};

export const LisTestMaster = () => {
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [testitemdataObj, setTestitemObj] = useState(initialtestitemDataObj);
  const [selectedRecord, setSelectedRecord] = useState();
  const [tubetypedropdown, setSpecimendropdown] = useState([]);
  const [categoryDropdown, setCategoryDropdown] = useState([]);
  const [unitDropdown, setUnitDropdown] = useState([]);
  const [helpdescriptiondialog, setHelpdescriptiondialog] = useState(false);
  const [refrencedialog, setRefrencedialog] = useState(false);
  const [refrencTextedialog, setRefrencTextedialog] = useState(false);
  const [refrencelist, setRefrencelist] = useState([]);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [input, setInput] = useState('');
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [originalData, setOriginalData] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);

  const [parameters, setParameters] = useState([]);
  const [interpretation, setInterpretation] = useState('');
  const [notes, setNotes] = useState('');
  const [comments, setComments] = useState('');

  // Add selected parameter state for dialogs
  const [selectedParameter, setSelectedParameter] = useState(null);



  const [sampleVolume, setSampleVolume] = useState({ amount: 0, unit: 'ml' });
  const [collectionInstructions, setCollectionInstructions] = useState([]);
  const [stability, setStability] = useState({ roomTemp: 0, refrigerated: 0, frozen: 0 });
  const [turnaroundTime, setTurnaroundTime] = useState({ routine: 0, stat: 0, unit: 'hours' });
  const [additionalTubes, setAdditionalTubes] = useState([]);
  const [cannotCollectWith, setCannotCollectWith] = useState([]);
  const [specialPreparation, setSpecialPreparation] = useState('');

  // Form default values matching backend schema
  const formDefaultValues = {
    test_name: '',
    shortName: '',
    price: 0,
    testType: 'single_parameter',
    method: '',
    instrument: '',
    tubeid: '',
    storagetype: "",
    lonicCode: "",
    category: '',
    status: 'active',
    formula: '',
    dependsOn: [],
    calculationType: 'arithmetic',
    isCalculated: false,
    decimalPlaces: 2,
    isVisible: true,
    sampleType: 'blood',
    sampleVolume: {
      amount: 0,
      unit: 'ml'
    },
    additionalTubes: [],
    collectionInstructions: [],
    fastingRequired: false,
    fastingHours: 0,
    specialPreparation: '',
    optimalCollectionTime: '',
    stability: {
      roomTemp: 0,
      refrigerated: 0,
      frozen: 0
    },
    cannotCollectWith: [],
    turnaroundTime: {
      routine: 0,
      stat: 0,
      unit: 'hours'
    },
  };

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        test_name: Yup.string().required('Test Name Is Required'),
        shortName: Yup.string().required('Short Name Is Required'),
        // price: Yup.number().required('Price Is Required').min(0, 'Price must be positive'),
        // testType: Yup.string().oneOf(['single_parameter', 'multi_parameter', 'multi_parameter_nested', 'document']).required('Test Type Is Required'),
        // tubeid: Yup.string().required('Tube Is Required'),
        // storagetype: Yup.string().required('Storage Is Required'),
        // lonicCode: Yup.string().required('Lonic Is Required'),
        status: Yup.string().oneOf(['active', 'inactive', 'deleted']).required('Status Is Required')
      })
    ),
    defaultValues: formDefaultValues,
    mode: 'all'
  });

  const gettubetypedropdown = useCallback(async () => {
    if (!selectedRecord) {
      try {

        const data = await dropdownFetch('tube-type');
        if (data && data.length > 0) {
          const [defaultSponsor] = data.filter(({ status }) => status === 'default');
          setValue('tubeid', defaultSponsor?._id || '');
        }
        setSpecimendropdown(data || []);
      } catch (error) {
        console.error('Error fetching tubetype dropdown:', error);
        setSpecimendropdown([]);
      }
    }
  }, [selectedRecord]);

  const getcategorydropdown = useCallback(async () => {
    if (!selectedRecord) {
      try {
        const data = await dropdownFetch('category');
        if (data && data.length > 0) {
          const [defaultSponsor] = data.filter(({ status }) => status === 'default');
          setValue('category', defaultSponsor?._id || '');
        }
        setCategoryDropdown(data || []);
      } catch (error) {
        console.error('Error fetching category dropdown:', error);
        setCategoryDropdown([]);
      }
    }
  }, [selectedRecord]);

  const getUnitdropdown = useCallback(async () => {
    try {
      const data = await dropdownFetch('unit');
      setUnitDropdown(data || []);
    } catch (error) {
      console.error('Error fetching unit dropdown:', error);
      setUnitDropdown([]);
    }
  }, []);

  useEffect(() => {
    getUnitdropdown();
    gettubetypedropdown();
    getcategorydropdown();
  }, [gettubetypedropdown, getcategorydropdown]);

  const { handleSubmit, setValue, reset, control, watch } = methods;

  const testType = useWatch({
    control,
    name: 'testType',
    defaultValue: 'single_parameter'
  });

  // Safe field value setter
  const safeSetValue = (key, value) => {
    if (key && value !== undefined && value !== null) {
      setValue(key, value);
    }
  };

  // Effect to handle parameter restrictions based on test type
  useEffect(() => {
    if (['new', 'edit'].includes(currentAction)) {
      let updatedParameters = [...parameters];

      switch (testType) {
        case 'single_parameter':
          // Only one parameter allowed, no groupBy
          if (updatedParameters.length > 1) {
            updatedParameters = [updatedParameters[0]];
          }
          if (updatedParameters.length === 0) {
            updatedParameters = [createNewParameter()];
          }
          // Remove groupBy from all parameters
          updatedParameters = updatedParameters.map(param => ({
            ...param,
            groupBy: ''
          }));
          break;

        case 'multi_parameter':
          // Multiple parameters allowed, no groupBy
          updatedParameters = updatedParameters.map(param => ({
            ...param,
            groupBy: ''
          }));
          break;

        case 'multi_parameter_nested':
          // Multiple parameters allowed with groupBy
          // Keep existing groupBy values
          break;

        case 'document':
          // Only one parameter with CKEditor for default result, no other fields
          if (updatedParameters.length > 1) {
            updatedParameters = [updatedParameters[0]];
          }
          if (updatedParameters.length === 0) {
            updatedParameters = [createNewParameter()];
          }
          // For document type, only keep defaultResult and set fixed values
          updatedParameters = updatedParameters.map(param => ({
            tempId: param.tempId || Date.now().toString(),
            name: 'Document Result',
            unit: '',
            inputType: 'numeric',
            defaultResult: param.defaultResult || '',
            referenceRangeText: '',
            criticalRangeText: '',
            helpText: '',
            groupBy: ''
          }));
          break;

        default:
          break;
      }

      setParameters(updatedParameters);
    }
  }, [testType, currentAction]);

  const createNewParameter = () => ({
    tempId: Date.now().toString(),
    name: '',
    unit: '',
    inputType: 'numeric',
    defaultResult: '',
    referenceRangeText: '',
    criticalRangeText: '',
    helpText: '',
    groupBy: '',
    referenceRanges: []
  });

  // FIXED: Properly handle selected record data loading
  useEffect(() => {
    if (selectedRecord) {
      // Reset form first to clear any previous values
      reset(formDefaultValues);

      // Safely set values from selected record - handle nested objects properly
      const fieldsToSet = {
        test_name: selectedRecord.test_name || '',
        shortName: selectedRecord.shortName || '',
        price: selectedRecord.price || 0,
        testType: selectedRecord.testType || 'single_parameter',
        method: selectedRecord.method || '',
        instrument: selectedRecord.instrument || '',
        tubeid: selectedRecord.tubeid?._id || selectedRecord.tubeid || '',
        storagetype: selectedRecord.storagetype || '',
        lonicCode: selectedRecord.lonicCode || '',
        category: selectedRecord.category?._id || selectedRecord.category || '',
        status: selectedRecord.status || 'active',

        // NEW COLLECTION FIELDS
        sampleType: selectedRecord.sampleType || 'blood',
        sampleVolume: selectedRecord.sampleVolume || { amount: 0, unit: 'ml' },
        fastingRequired: selectedRecord.fastingRequired || false,
        fastingHours: selectedRecord.fastingHours || 0,
        optimalCollectionTime: selectedRecord.optimalCollectionTime || '',
      };

      // Set form values
      Object.entries(fieldsToSet).forEach(([key, value]) => {
        safeSetValue(key, value);
      });

      // Set state variables for complex objects
      setCollectionInstructions(selectedRecord.collectionInstructions || []);
      setStability(selectedRecord.stability || { roomTemp: 0, refrigerated: 0, frozen: 0 });
      setTurnaroundTime(selectedRecord.turnaroundTime || { routine: 0, stat: 0, unit: 'hours' });
      setAdditionalTubes(selectedRecord.additionalTubes || []);
      setCannotCollectWith(selectedRecord.cannotCollectWith || []);
      setSpecialPreparation(selectedRecord.specialPreparation || '');

      // Set parameters from selected record with safety checks
      if (selectedRecord.parameters && Array.isArray(selectedRecord.parameters)) {
        const formattedParameters = selectedRecord.parameters.map(param => ({
          ...param,
          _id: param._id,
          tempId: param._id || param.tempId || Date.now().toString(),
          inputType: param.inputType || 'numeric',
          unit: param.unit || '',
          name: param.name || '',
          defaultResult: param.defaultResult || '',
          referenceRangeText: param.referenceRangeText || '',
          criticalRangeText: param.criticalRangeText || '',
          helpDescriptions: param.helpDescriptions || '',
          groupBy: param.groupBy || '',
          referenceRanges: param.referenceRanges || []
        }));
        setParameters(formattedParameters);

        // Set reference ranges from parameters
        const allReferenceRanges = selectedRecord.parameters.flatMap(param =>
          (param.referenceRanges || []).map(range => ({
            ...range,
            parameterId: param._id || param.tempId,
            parameterName: param.name
          }))
        );
        setRefrencelist(allReferenceRanges);
      } else {
        setParameters([]);
        setRefrencelist([]);
      }

      // Set additional text fields
      setInterpretation(selectedRecord.interpretation || '');
      setNotes(selectedRecord.notes || '');
      setComments(selectedRecord.comments || '');

    } else {
      // Reset all states when no record is selected
      reset(formDefaultValues);
      setParameters([]);
      setInterpretation('');
      setNotes('');
      setComments('');
      setRefrencelist([]);
      setSelectedParameter(null);

      // Reset collection states
      setCollectionInstructions([]);
      setStability({ roomTemp: 0, refrigerated: 0, frozen: 0 });
      setTurnaroundTime({ routine: 0, stat: 0, unit: 'hours' });
      setAdditionalTubes([]);
      setCannotCollectWith([]);
      setSpecialPreparation('');
    }
  }, [selectedRecord, reset]);

  const formSubmit = async (formValues) => {
    console.log("Form Values:", formValues);
    setLoading(true);
    let editFlag = false;

    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }

    // Clean empty values
    Object.keys(formValues).forEach(key => {
      if (formValues[key] === "") {
        delete formValues[key];
      }
    });

    try {
      // Prepare parameters according to new schema
      const preparedParameters = parameters.map(param => {
        const parameterRefRanges = refrencelist.filter(ref =>
          ref.parameterId === param._id || ref.parameterId === param.tempId
        ).map(({ parameterId, parameterName, ...range }) => range);

        return {
          // Backend will auto-generate 'name' from 'label' if not provided
          label: param.label || '',
          name: param.name || '', // Will be auto-generated if empty
          unit: param.unit || '',
          inputType: param.inputType || 'numeric',
          defaultResult: param.defaultResult || '',
          referenceRangeText: param.referenceRangeText || '',
          helpDescriptions: param.helpDescriptions || '',
          groupBy: param.groupBy || '',
          referenceRanges: parameterRefRanges,

          // New formula fields
          formula: param.formula || '',
          dependsOn: param.dependsOn || [],
          calculationType: param.calculationType || 'arithmetic',
          isCalculated: param.isCalculated || false,
          decimalPlaces: param.decimalPlaces || 2,
          isVisible: param.isVisible !== false
        };
      });

      // Prepare the complete payload
      const payload = {
        ...formValues,
        parameters: preparedParameters,
        interpretation: interpretation || '',
        notes: notes || '',
        comments: comments || '',

        // NEW COLLECTION FIELDS
        collectionInstructions: collectionInstructions,
        stability: stability,
        turnaroundTime: turnaroundTime,
        additionalTubes: additionalTubes,
        cannotCollectWith: cannotCollectWith,
        specialPreparation: specialPreparation,
      };

      // Remove empty arrays and objects
      Object.keys(payload).forEach(key => {
        if (Array.isArray(payload[key]) && payload[key].length === 0) {
          delete payload[key];
        }
        if (typeof payload[key] === 'object' && payload[key] !== null && Object.keys(payload[key]).length === 0) {
          delete payload[key];
        }
        // Remove empty string values
        if (payload[key] === '') {
          delete payload[key];
        }
      });

      // Clean nested objects
      if (payload.sampleVolume && (!payload.sampleVolume.amount || payload.sampleVolume.amount === 0)) {
        delete payload.sampleVolume;
      }

      console.log("Final Payload:", payload);

      // Use the new API endpoints
      const endpoint = editFlag ? '/lis-test/update/' + selectedRecord._id : '/lis-test/create';
      const method = editFlag ? 'PUT' : 'POST';

      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result?.success) {
        toast(`Successfully ${editFlag ? 'updated' : 'created'} Lab Test`, { variant: 'success' });
        setSelectedRecord(result.data);
        setCurrentAction();
        reset(formDefaultValues);
        setParameters([]);
        setRefrencelist([]);
        setInterpretation('');
        setNotes('');
        setComments('');
        setSelectedParameter(null);

        // Reset collection states
        setCollectionInstructions([]);
        setStability({ roomTemp: 0, refrigerated: 0, frozen: 0 });
        setTurnaroundTime({ routine: 0, stat: 0, unit: 'hours' });
        setAdditionalTubes([]);
        setCannotCollectWith([]);
        setSpecialPreparation('');
      } else {
        toast(result?.error || `Failed to ${editFlag ? 'update' : 'create'} Lab Test`, {
          variant: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast('Error submitting form: ' + error.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    try {
      const response = await request('/lab-test-cancel', {
        method: 'PUT',
        params: selectedRecord._id,
        body: { status: 'inactive' }
      });
      if (response?.success) {
        toast('Successfully Cancelled Lab Test', { variant: 'success' });
        setSelectedRecord();
        reset(formDefaultValues);
        setParameters([]);
        setInterpretation('');
        setNotes('');
        setComments('');
        setSelectedParameter(null);
      } else {
        toast('Failed to cancel Lab Test', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error cancelling record:', error);
      toast('Error cancelling record', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    try {
      const response = await request('/lis-test-delete', {
        method: 'delete',
        params: selectedRecord._id,
        body: {
          status: 'deleted',
          isDeleted: true,
          deletedAt: new Date()
        }
      });
      if (response?.success) {
        toast('Successfully deleted Lab Test', { variant: 'success' });
        setSelectedRecord();
        reset(formDefaultValues);
        setParameters([]);
        setInterpretation('');
        setNotes('');
        setComments('');
        setSelectedParameter(null);
      } else {
        toast('Failed to delete Lab Test', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error deleting record:', error);
      toast('Error deleting record', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Dialog handlers with proper parameter selection
  const handleAddHelpDescription = (data) => {
    if (!data || !data.parameterId) {
      toast('Invalid help description data', { variant: 'error' });
      return;
    }

    const updatedParameters = parameters.map(param => {
      if (param._id === data.parameterId || param.tempId === data.parameterId) {
        return {
          ...param,
          helpDescriptions: data.helpText
        };
      }
      return param;
    });

    setParameters(updatedParameters);
    setHelpdescriptiondialog(false);
    setSelectedParameter(null);
  };

  const handleAddReferenceRangeText = (data) => {
    if (!data || !data.parameterId) {
      toast('Invalid reference range text data', { variant: 'error' });
      return;
    }

    const updatedParameters = parameters.map(param => {
      if (param._id === data.parameterId || param.tempId === data.parameterId) {
        return {
          ...param,
          referenceRangeText: data.referenceRangeText
        };
      }
      return param;
    });

    setParameters(updatedParameters);
    setRefrencTextedialog(false);
    setSelectedParameter(null);
  };

  const handleAddRefrenceRange = (data) => {
    console.log('=== HANDLING REFERENCE RANGE SUBMISSION ===');
    console.log('Submitted data:', data);
    console.log('Is update:', data._isUpdate);

    if (!data || !data.parameterId) {
      console.error('Invalid reference range data - missing parameterId');
      toast('Invalid reference range data', { variant: 'error' });
      return;
    }

    // Check if this is an update operation
    if (data._isUpdate) {
      // Update existing range - find by _id or tempId
      const existingRangeIndex = refrencelist.findIndex(ref =>
        ref._id === data._id || ref.tempId === data.tempId
      );

      if (existingRangeIndex > -1) {
        // Update existing range
        const updatedList = [...refrencelist];
        updatedList[existingRangeIndex] = {
          ...data,
          parameterName: parameters.find(p => p._id === data.parameterId || p.tempId === data.parameterId)?.name || ''
        };
        console.log('Updating existing range at index:', existingRangeIndex);
        setRefrencelist(updatedList);
      } else {
        console.error('Range to update not found');
        toast('Range to update not found', { variant: 'error' });
      }
    } else {
      // Add new range
      const newRange = {
        ...data,
        parameterName: parameters.find(p => p._id === data.parameterId || p.tempId === data.parameterId)?.name || ''
      };
      console.log('Adding new range:', newRange);
      setRefrencelist([...refrencelist, newRange]);
    }
  };

  const handleTestFormulas = async () => {
    try {
      // Create sample values for testing - only for non-calculated parameters
      const sampleValues = {};
      parameters.forEach(param => {
        if (!param.isCalculated && param.name && param.inputType === 'numeric') {
          // Provide realistic sample values based on parameter type
          sampleValues[param.name] = 10; // Default sample value
        }
      });

      console.log('Testing formulas with sample values:', sampleValues);

      const response = await fetch(`${BASE_URL}/lis-test/test-formulas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parameters: parameters.filter(p => p.name), // Only include parameters with names
          sampleValues: sampleValues
        })
      });

      const result = await response.json();

      if (result.success) {
        toast('All formulas tested successfully!', { variant: 'success' });
        console.log('Formula test results:', result.data);
      } else {
        const errorMessage = result.data?.errors?.[0]?.error || result.error || 'Formula test failed';
        toast(`Formula test failed: ${errorMessage}`, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error testing formulas:', error);
      toast('Error testing formulas: ' + error.message, { variant: 'error' });
    }
  };

  const handleAddParameter = () => {
    if (testType === 'single_parameter') {
      toast('Single parameter test can only have one parameter', { variant: 'warning' });
      return;
    }

    if (testType === 'document') {
      toast('Document test can only have one parameter', { variant: 'warning' });
      return;
    }

    const newParameter = createNewParameter();
    setParameters([...parameters, newParameter]);
  };

  const handleUpdateParameter = useCallback((index, field, value) => {
    console.log(`Updating parameter ${index}, field: ${field}, value:`, value);

    setParameters(prevParameters => {
      if (!prevParameters[index]) return prevParameters;

      const updatedParameters = prevParameters.map((param, i) => {
        if (i === index) {
          const updatedParam = {
            ...param,
            [field]: value
          };
          console.log('Updated param:', updatedParam);
          return updatedParam;
        }
        return param;
      });

      return updatedParameters;
    });
  }, []);

  const handleRemoveParameter = (index) => {
    if (index >= 0 && index < parameters.length) {
      if (testType === 'single_parameter' && parameters.length === 1) {
        toast('Single parameter test must have at least one parameter', { variant: 'warning' });
        return;
      }

      if (testType === 'document' && parameters.length === 1) {
        toast('Document test must have at least one parameter', { variant: 'warning' });
        return;
      }

      const parameterToRemove = parameters[index];

      // Remove associated reference ranges
      const updatedRefRanges = refrencelist.filter(ref =>
        ref.parameterId !== parameterToRemove._id && ref.parameterId !== parameterToRemove.tempId
      );
      setRefrencelist(updatedRefRanges);

      // Remove parameter
      const updatedParameters = parameters.filter((_, i) => i !== index);
      setParameters(updatedParameters);
    }
  };

  // FIXED: Dialog open handlers with parameter selection
  const handleOpenHelpDialog = () => {
    if (parameters.length === 0) {
      toast('No parameters available', { variant: 'warning' });
      return;
    }
    if (parameters.length === 1) {
      // Auto-select the only parameter
      const param = parameters[0];
      setSelectedParameter(param._id || param.tempId);
    }
    setHelpdescriptiondialog(true);
  };

  const handleOpenReferenceRangeDialog = () => {
    console.log('=== OPENING REFERENCE RANGE DIALOG ===');
    console.log('Current parameters:', parameters);
    console.log('Current refrencelist:', refrencelist);
    console.log('Current selectedParameter:', selectedParameter);

    if (parameters.length === 0) {
      toast('No parameters available', { variant: 'warning' });
      return;
    }

    // If only one parameter, auto-select it
    if (parameters.length >= 1) {
      const param = parameters[0];
      const paramId = param._id || param.tempId;
      console.log('Auto-selecting single parameter:', param.name, 'ID:', paramId);
      setSelectedParameter(paramId);

      // Show existing ranges for this parameter
      const existingRanges = refrencelist.filter(ref => ref.parameterId === paramId);
      console.log('Existing ranges for this parameter:', existingRanges);
    } else {
      // For multiple parameters, show a message that user needs to select one
      console.log('Multiple parameters - user needs to select one');
      // Let the user select from the dropdown in the form
    }

    setRefrencedialog(true);
  };

  const handleOpenReferenceTextDialog = () => {
    if (parameters.length === 0) {
      toast('No parameters available', { variant: 'warning' });
      return;
    }
    if (parameters.length === 1) {
      // Auto-select the only parameter
      const param = parameters[0];
      setSelectedParameter(param._id || param.tempId);
    }
    setRefrencTextedialog(true);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setTestitemObj({ loading: true });
    try {
      const response = await axios.get(`${BASE_URL}/lis-test/list?limit=${10}`);
      if (response?.data.success) {
        setTestitemObj({ loading: false, data: response.data.data });
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setTestitemObj({ loading: false, data: [] });
      }
    } catch (error) {
      console.error('Error during fetching:', error);
      toast('Error in fetching data', { variant: 'error' });
      setTestitemObj({ loading: false, data: [] });
    }
  };

  useEffect(() => {
    if (input !== undefined) {
      handleTestItemSearch(input);
    }
  }, [input]);

  const handleTestItemSearch = async (searchInput) => {
    const lowercasedInput = (searchInput || '').toLowerCase().trim();

    try {
      const response = await request('/lis-test-list', {
        method: 'GET',
        query: { search: lowercasedInput, status: 'active' }
      });
      if (response.success && response.data) {
        setTestitemObj({ loading: false, data: response.data });
      } else {
        setTestitemObj({ loading: false, data: [] });
      }
    } catch (error) {
      console.error('Error during search:', error);
      setTestitemObj({ loading: false, data: [] });
    }
  };

  const handleChange = (e) => {
    const newInput = e.target.value || '';
    setInput(newInput);


    console.log(newInput);
  };

  // CKEditor configuration
  const editorConfiguration = {
    toolbar: {
      items: [
        // Document structure
        'undo', 'redo', '|',
        'heading', 'style', 'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', '|',

        // Basic text formatting
        'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', 'code', '|',

        // Paragraph & layout
        'alignment', 'outdent', 'indent', 'blockQuote', 'horizontalLine', '|',

        // Lists
        'bulletedList', 'numberedList', 'todoList', '|',

        // Links & media
        'link', 'insertImage', 'insertTable', 'mediaEmbed', 'codeBlock', '|',

        // Special content
        'specialCharacters', 'pageBreak', 'highlight', 'removeFormat', '|',

        // Content structure & design
        'sourceEditing', 'htmlEmbed', 'findAndReplace', 'selectAll', 'showBlocks', '|',

        // Table tools
        'tableColumn', 'tableRow', 'mergeTableCells', '|',

        // Optional design controls
        'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', 'alignment', '|',

        // Accessibility & content design
        'exportPdf', 'exportWord', 'wordCount'
      ],
      shouldNotGroupWhenFull: true
    },
    image: {
      toolbar: [
        'imageStyle:alignLeft', 'imageStyle:alignCenter', 'imageStyle:alignRight',
        '|', 'imageResize', '|', 'linkImage'
      ],
      styles: [
        'full', 'side', 'alignLeft', 'alignCenter', 'alignRight'
      ]
    },
    table: {
      contentToolbar: [
        'tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'
      ]
    },
    mediaEmbed: {
      previewsInData: true
    },
    link: {
      decorators: {
        openInNewTab: {
          mode: 'manual',
          label: 'Open in a new tab',
          defaultValue: true,
          attributes: {
            target: '_blank',
            rel: 'noopener noreferrer'
          }
        }
      }
    },
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
        { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
        { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
      ]
    },
    fontFamily: {
      options: [
        'default', 'Arial, Helvetica, sans-serif',
        'Courier New, Courier, monospace',
        'Georgia, serif',
        'Lucida Sans Unicode, Lucida Grande, sans-serif',
        'Tahoma, Geneva, sans-serif',
        'Times New Roman, Times, serif',
        'Trebuchet MS, Helvetica, sans-serif',
        'Verdana, Geneva, sans-serif'
      ]
    },
    fontSize: {
      options: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36],
      supportAllValues: true
    },
    alignment: {
      options: ['left', 'center', 'right', 'justify']
    },
    htmlEmbed: {
      showPreviews: true
    },
    wordCount: {
      onUpdate: stats => {
        console.log(`Characters: ${stats.characters}, Words: ${stats.words}`);
      }
    }
  };


  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none', backgroundColor: '#f8f9fa' }}>
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            {/* Enhanced Header Section */}
            <Card sx={styles.mainCard}>
              <Box sx={styles.header}>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <ScienceIcon sx={{ fontSize: '2.5rem' }} />
                    <Typography variant="h3" sx={{ fontWeight: 800, fontSize: '2.2rem' }}>
                      Lab Test Master
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <CustomButtonn
                      id={`${lastPathSegment}Write`}
                      variant="contained"
                      disabled={['edit', 'new'].includes(currentAction)}
                      onClick={() => {
                        setSelectedRecord();
                        setCurrentAction('new');
                        reset(formDefaultValues);
                        setParameters([]);
                        setInterpretation('');
                        setNotes('');
                        setComments('');
                        setRefrencelist([]);
                        setSelectedParameter(null);
                      }}
                      startIcon={<AddIcon />}
                      sx={styles.actionButton}
                    >
                      New Test
                    </CustomButtonn>
                    <CustomButtonn
                      id={`${lastPathSegment}Update`}
                      variant="contained"
                      disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                      onClick={() => setCurrentAction('edit')}
                      startIcon={<EditIcon />}
                      sx={styles.actionButton}
                    >
                      Edit
                    </CustomButtonn>
                    <CustomButtonn
                      id={'Submit'}
                      type="submit"
                      variant="contained"
                      disabled={!['edit', 'new'].includes(currentAction) || loading}
                      onClick={handleSubmit(formSubmit)}
                      startIcon={<SaveIcon />}
                      sx={styles.actionButton}
                    >
                      Save
                    </CustomButtonn>
                    <CustomButtonn
                      id={'Cancel'}
                      variant="contained"
                      disabled={['edit', 'new'].includes(currentAction) || loading || !selectedRecord}
                      onClick={handleCancelRecord}
                      startIcon={<CancelIcon />}
                      sx={styles.actionButton}
                    >
                      Cancel
                    </CustomButtonn>
                    <CustomButtonn
                      id={`isSuperAdmin`}
                      variant="contained"
                      disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                      onClick={handleDeleteRecord}
                      startIcon={<DeleteIcon />}
                      sx={styles.actionButton}
                    >
                      Delete
                    </CustomButtonn>
                    <CustomButtonn
                      id={`${lastPathSegment}Read`}
                      variant="contained"
                      disabled={['new', 'edit'].includes(currentAction)}
                      onClick={handleQueryWindow}
                      startIcon={<SearchIcon />}
                      sx={styles.actionButton}
                    >
                      Query
                    </CustomButtonn>
                    <CustomButtonn
                      id={`${lastPathSegment}Print`}
                      variant="contained"
                      disabled={!selectedRecord || currentAction}
                      startIcon={<DescriptionIcon />}
                      sx={styles.actionButton}
                    >
                      Print
                    </CustomButtonn>
                    <CustomButtonn
                      id={'Exit'}
                      variant="contained"
                      onClick={() => {
                        setCurrentAction('');
                        setSelectedRecord();
                        reset(formDefaultValues);
                        setSelectedParameter(null);
                      }}
                      startIcon={<ExitToAppIcon />}
                      sx={styles.actionButton}
                    >
                      Exit
                    </CustomButtonn>
                  </Stack>
                </Stack>
              </Box>

              <CardContent sx={{ p: 3 }}>
                {/* Basic Information Section */}
                <Card sx={styles.sectionCard}>
                  <CardHeader
                    title="Basic Test Information"
                    titleTypographyProps={{
                      variant: 'h4',
                      fontWeight: 700,
                      fontSize: '1.8rem',
                      color: '#2c3e50'
                    }}
                    sx={{
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={3}>
                      {/* Column 1 */}
                      <Grid item xs={4}>
                        <Stack spacing={2}>
                          <Box>
                            <Typography sx={styles.formLabel}>Test Name</Typography>
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="test_name"
                              placeholder="Enter Test Name"
                              sx={styles.inputField}
                              fullWidth
                            />
                          </Box>
                          <Box>
                            <Typography sx={styles.formLabel}>Short Name</Typography>
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="shortName"
                              placeholder="Enter Short Name"
                              sx={styles.inputField}
                              fullWidth
                            />
                          </Box>
                          <Box>
                            <Typography sx={styles.formLabel}>Price</Typography>
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="price"
                              type="number"
                              placeholder="Enter Price"
                              sx={styles.inputField}
                              fullWidth
                            />
                          </Box>
                          <Box>
                            <Typography sx={styles.formLabel}>Storage Type</Typography>
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="storagetype"
                              placeholder="Enter Storage Type"
                              sx={styles.inputField}
                              fullWidth
                            />
                          </Box>
                        </Stack>
                      </Grid>

                      {/* Column 2 */}
                      <Grid item xs={4}>
                        <Stack spacing={2}>
                          <Box>
                            <Typography sx={styles.formLabel}>Method</Typography>
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="method"
                              placeholder="Enter Method"
                              sx={styles.inputField}
                              fullWidth
                            />
                          </Box>
                          <Box>
                            <Typography sx={styles.formLabel}>Instrument</Typography>
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="instrument"
                              placeholder="Enter Instrument"
                              sx={styles.inputField}
                              fullWidth
                            />
                          </Box>
                          <Box>
                            <Typography sx={styles.formLabel}>Test Type</Typography>
                            <SelectTag
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="testType"
                              menus={[
                                { _id: 'single_parameter', name: 'Single Parameter' },
                                { _id: 'multi_parameter', name: 'Multi Parameter' },
                                { _id: 'multi_parameter_nested', name: 'Multi Parameter Nested' },
                                { _id: 'document', name: 'Document' }
                              ]}
                              formControlSX={styles.selectField}
                              fullWidth
                            />
                          </Box>
                          <Box>
                            <Typography sx={styles.formLabel}>Lonic Code</Typography>
                            <TextField
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="lonicCode"
                              placeholder="Enter Lonic Code"
                              sx={styles.inputField}
                              fullWidth
                            />
                          </Box>
                        </Stack>
                      </Grid>

                      {/* Column 3 */}
                      <Grid item xs={4}>
                        <Stack spacing={2}>
                          <Box>
                            <Typography sx={styles.formLabel}>Tube Type</Typography>
                            <SelectTag
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="tubeid"
                              menus={tubetypedropdown.map((data) => ({ ...data, name: data.color }))}
                              formControlSX={styles.selectField}
                              fullWidth
                            />
                          </Box>
                          <Box>
                            <Typography sx={styles.formLabel}>Category</Typography>
                            <SelectTag
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="category"
                              menus={categoryDropdown.map((data) => ({ ...data, name: data.category_name }))}
                              formControlSX={styles.selectField}
                              fullWidth
                            />
                          </Box>
                          <Box>
                            <Typography sx={styles.formLabel}>Status</Typography>
                            <SelectTag
                              disabled={!['new', 'edit'].includes(currentAction)}
                              name="status"
                              menus={[
                                { _id: 'active', name: 'Active' },
                                { _id: 'inactive', name: 'Inactive' },
                                { _id: 'deleted', name: 'Deleted' }
                              ]}
                              formControlSX={styles.selectField}
                              fullWidth
                            />
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Test Type Information Alert */}
                <Card sx={styles.sectionCard}>
                  <CardContent>
                    {testType === 'single_parameter' && (
                      <Alert severity="info" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                          Single Parameter Test: Only one parameter allowed. Group By field is disabled.
                        </Typography>
                      </Alert>
                    )}
                    {testType === 'multi_parameter' && (
                      <Alert severity="info" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                          Multi Parameter Test: Multiple parameters allowed. Group By field is disabled. Formula calculations supported.
                        </Typography>
                      </Alert>
                    )}
                    {testType === 'multi_parameter_nested' && (
                      <Alert severity="info" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                          Multi Parameter Nested Test: Multiple parameters allowed with Group By functionality. Formula calculations supported.
                        </Typography>
                      </Alert>
                    )}
                    {testType === 'document' && (
                      <Alert severity="info" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                          Document Test: Only one parameter allowed. Only CKEditor for document content is shown.
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
                {/* Sample Collection Section */}
                <Card sx={styles.sectionCard}>
                  <CardHeader
                    title="Sample Collection Details"
                    titleTypographyProps={{
                      variant: 'h4',
                      fontWeight: 700,
                      fontSize: '1.8rem',
                      color: '#2c3e50'
                    }}
                    sx={{
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={4}>
                        <Typography sx={styles.formLabel}>Sample Type</Typography>
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="sampleType"
                          menus={[
                            { _id: 'blood', name: 'Blood' },
                            { _id: 'urine', name: 'Urine' },
                            { _id: 'stool', name: 'Stool' },
                            { _id: 'sputum', name: 'Sputum' },
                            { _id: 'csf', name: 'CSF' },
                            { _id: 'tissue', name: 'Tissue' },
                            { _id: 'swab', name: 'Swab' },
                            { _id: 'other', name: 'Other' }
                          ]}
                          formControlSX={styles.selectField}
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <Typography sx={styles.formLabel}>Sample Volume</Typography>
                        <Stack direction="row" spacing={1}>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            name="sampleVolume.amount"
                            type="number"
                            placeholder="Amount"
                            sx={styles.inputField}
                            fullWidth
                          />
                          <SelectTag
                            disabled={!['new', 'edit'].includes(currentAction)}
                            name="sampleVolume.unit"
                            menus={[
                              { _id: 'ml', name: 'ml' },
                              { _id: 'l', name: 'L' },
                              { _id: 'mg', name: 'mg' },
                              { _id: 'g', name: 'g' }
                            ]}
                            formControlSX={styles.selectField}
                            sx={{ minWidth: '100px' }}
                          />
                        </Stack>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography sx={styles.formLabel}>Additional Tubes</Typography>
                        <SelectTag
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="additionalTubes"
                          menus={tubetypedropdown.map((data) => ({ ...data, name: data.color }))}
                          formControlSX={styles.selectField}
                          fullWidth
                          multiple
                        />
                      </Grid>
                    </Grid>

                    {/* Fasting Requirements */}
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="fastingRequired"
                              disabled={!['new', 'edit'].includes(currentAction)}
                              sx={{ '& .MuiSvgIcon-root': { fontSize: '1.8rem' } }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                              Fasting Required
                            </Typography>
                          }
                        />
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="fastingHours"
                          type="number"
                          placeholder="Fasting Hours"
                          sx={styles.inputField}
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Typography sx={styles.formLabel}>Optimal Collection Time</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="optimalCollectionTime"
                          placeholder="e.g., Morning, Fasting"
                          sx={styles.inputField}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>


                {/* Stability & Turnaround Time Section */}
                <Card sx={styles.sectionCard}>
                  <CardHeader
                    title="Stability & Turnaround Time"
                    titleTypographyProps={{
                      variant: 'h4',
                      fontWeight: 700,
                      fontSize: '1.8rem',
                      color: '#2c3e50'
                    }}
                    sx={{
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={4}>
                        <Typography sx={styles.formLabel}>Room Temp Stability (hours)</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="stability.roomTemp"
                          type="number"
                          placeholder="Hours"
                          sx={styles.inputField}
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <Typography sx={styles.formLabel}>Refrigerated Stability (hours)</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="stability.refrigerated"
                          type="number"
                          placeholder="Hours"
                          sx={styles.inputField}
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <Typography sx={styles.formLabel}>Frozen Stability (hours)</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="stability.frozen"
                          type="number"
                          placeholder="Hours"
                          sx={styles.inputField}
                          fullWidth
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mt: 2 }}>
                      <Grid item xs={6}>
                        <Typography sx={styles.formLabel}>Routine Turnaround (hours)</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="turnaroundTime.routine"
                          type="number"
                          placeholder="Hours"
                          sx={styles.inputField}
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Typography sx={styles.formLabel}>STAT Turnaround (hours)</Typography>
                        <TextField
                          disabled={!['new', 'edit'].includes(currentAction)}
                          name="turnaroundTime.stat"
                          type="number"
                          placeholder="Hours"
                          sx={styles.inputField}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Collection Instructions Section */}
                <Card sx={styles.sectionCard}>
                  <CardHeader
                    title="Collection Instructions"
                    titleTypographyProps={{
                      variant: 'h4',
                      fontWeight: 700,
                      fontSize: '1.8rem',
                      color: '#2c3e50'
                    }}
                    action={
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={!['new', 'edit'].includes(currentAction)}
                        onClick={() => setCollectionInstructions([...collectionInstructions, { instruction: '', type: 'during_collection', critical: false }])}
                        startIcon={<AddIcon />}
                        sx={styles.actionButton}
                      >
                        Add Instruction
                      </Button>
                    }
                    sx={{
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <CardContent>
                    {collectionInstructions.map((instruction, index) => (
                      <Card key={index} sx={{ ...styles.parameterCard, mb: 2 }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={5}>
                              <Typography sx={styles.formLabel}>Instruction</Typography>
                              <MuiTextField
                                disabled={!['new', 'edit'].includes(currentAction)}
                                value={instruction.instruction}
                                onChange={(e) => {
                                  const updated = [...collectionInstructions];
                                  updated[index].instruction = e.target.value;
                                  setCollectionInstructions(updated);
                                }}
                                fullWidth
                                size="medium"
                                sx={styles.inputField}
                              />
                            </Grid>

                            <Grid item xs={3}>
                              <Typography sx={styles.formLabel}>Type</Typography>
                              <MuiTextField
                                select
                                disabled={!['new', 'edit'].includes(currentAction)}
                                value={instruction.type}
                                onChange={(e) => {
                                  const updated = [...collectionInstructions];
                                  updated[index].type = e.target.value;
                                  setCollectionInstructions(updated);
                                }}
                                fullWidth
                                size="medium"
                                sx={styles.selectField}
                              >
                                <MenuItem value="preparation">Preparation</MenuItem>
                                <MenuItem value="during_collection">During Collection</MenuItem>
                                <MenuItem value="post_collection">Post Collection</MenuItem>
                                <MenuItem value="storage">Storage</MenuItem>
                              </MuiTextField>
                            </Grid>

                            <Grid item xs={2}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={instruction.critical}
                                    onChange={(e) => {
                                      const updated = [...collectionInstructions];
                                      updated[index].critical = e.target.checked;
                                      setCollectionInstructions(updated);
                                    }}
                                    disabled={!['new', 'edit'].includes(currentAction)}
                                    sx={{ '& .MuiSvgIcon-root': { fontSize: '1.8rem' } }}
                                    name="instruction.critical"
                                  />
                                }
                                label={
                                  <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                                    Critical
                                  </Typography>
                                }
                              />
                            </Grid>

                            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'end' }}>
                              <Button
                                color="error"
                                variant="contained"
                                disabled={!['new', 'edit'].includes(currentAction)}
                                onClick={() => {
                                  const updated = collectionInstructions.filter((_, i) => i !== index);
                                  setCollectionInstructions(updated);
                                }}
                                startIcon={<DeleteIcon />}
                                sx={styles.actionButton}
                              >
                                Remove
                              </Button>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}

                    {collectionInstructions.length === 0 && (
                      <Alert severity="info" sx={{ fontSize: '1.2rem' }}>
                        No collection instructions added. Click "Add Instruction" to add instructions.
                      </Alert>
                    )}
                  </CardContent>
                </Card>

                {/* Parameters Section */}
                <Card sx={styles.sectionCard}>
                  <CardHeader
                    title="Test Parameters"
                    titleTypographyProps={{
                      variant: 'h4',
                      fontWeight: 700,
                      fontSize: '1.8rem',
                      color: '#2c3e50'
                    }}
                    action={
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={!['new', 'edit'].includes(currentAction) ||
                            testType === 'single_parameter' ||
                            testType === 'document'}
                          onClick={handleAddParameter}
                          startIcon={<AddIcon />}
                          sx={styles.actionButton}
                        >
                          Add Parameter
                        </Button>
                        <Button
                          variant="contained"
                          color="warning"
                          disabled={!['new', 'edit'].includes(currentAction) || parameters.length === 0 || testType === 'document'}
                          onClick={handleOpenReferenceRangeDialog}
                          sx={styles.actionButton}
                        >
                          Reference Ranges
                        </Button>
                        <Button
                          variant="contained"
                          color="warning"
                          disabled={!['new', 'edit'].includes(currentAction) || parameters.length === 0 || testType === 'document'}
                          onClick={handleOpenReferenceTextDialog}
                          sx={styles.actionButton}
                        >
                          Reference Text
                        </Button>
                        <Button
                          variant="contained"
                          color="warning"
                          disabled={!['new', 'edit'].includes(currentAction) || parameters.length === 0 || testType === 'document'}
                          onClick={handleOpenHelpDialog}
                          sx={styles.actionButton}
                        >
                          Help Descriptions
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          disabled={!['new', 'edit'].includes(currentAction) ||
                            !parameters.some(p => p.isCalculated) ||
                            testType === 'document'}
                          onClick={handleTestFormulas}
                          sx={styles.actionButton}
                        >
                          Test Formulas
                        </Button>
                      </Stack>
                    }
                    sx={{
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <CardContent>
                    {parameters.map((param, index) => (
                      <Card key={param.tempId} sx={styles.parameterCard}>
                        <CardContent>
                          {testType === 'document' ? (
                            // Document Test Type: Only show CKEditor
                            <Box>
                              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: '1.4rem' }}>
                                Document Content
                              </Typography>
                              <Box sx={{ border: '2px solid #e0e0e0', borderRadius: 2 }}>
                                <CKEditor
                                  editor={ClassicEditor}
                                  config={editorConfiguration}
                                  data={param.defaultResult || ''}
                                  disabled={!['new', 'edit'].includes(currentAction)}
                                  onChange={(event, editor) => {
                                    const data = editor.getData();
                                    handleUpdateParameter(index, 'defaultResult', data);
                                  }}
                                />
                              </Box>
                            </Box>
                          ) : (
                            // Enhanced Parameter Form with Formula Support
                            <Grid container spacing={2}>
                              {/* Basic Parameter Information */}
                              <Grid item xs={3}>
                                <Typography sx={styles.formLabel}>Parameter Label</Typography>
                                <MuiTextField
                                  disabled={!['new', 'edit'].includes(currentAction)}
                                  value={param.label || ''}
                                  onChange={(e) => {
                                    handleUpdateParameter(index, 'label', e.target.value);
                                    // Auto-generate name from label
                                    if (e.target.value.trim()) {
                                      const generatedName = e.target.value
                                        .toLowerCase()
                                        .replace(/[^a-zA-Z0-9\s]/g, '')
                                        .replace(/\s+/g, '_')
                                        .replace(/^_+|_+$/g, '');
                                      handleUpdateParameter(index, 'name', generatedName);
                                    }
                                  }}
                                  fullWidth
                                  size="medium"
                                  sx={styles.inputField}
                                  helperText={param.name ? `Internal name: ${param.name}` : 'Internal name will be auto-generated'}
                                />
                              </Grid>

                              <Grid item xs={2}>
                                <Typography sx={styles.formLabel}>Unit</Typography>
                                <MuiTextField
                                  select
                                  disabled={!['new', 'edit'].includes(currentAction)}
                                  value={param.unit || ''}
                                  onChange={(e) => handleUpdateParameter(index, 'unit', e.target.value)}
                                  fullWidth
                                  size="medium"
                                  sx={styles.selectField}
                                >
                                  <MenuItem value="">
                                    <em>Select Unit</em>
                                  </MenuItem>
                                  {unitDropdown.map((unit) => (
                                    <MenuItem key={unit._id} value={unit.unit_name}>
                                      {unit.unit_name}
                                    </MenuItem>
                                  ))}
                                </MuiTextField>
                              </Grid>

                              <Grid item xs={2}>
                                <Typography sx={styles.formLabel}>Input Type</Typography>
                                <MuiTextField
                                  select
                                  disabled={!['new', 'edit'].includes(currentAction)}
                                  value={param.inputType || 'numeric'}
                                  onChange={(e) => {
                                    const newInputType = e.target.value;
                                    handleUpdateParameter(index, 'inputType', newInputType);
                                    // Auto-set isCalculated for calculated types
                                    if (newInputType === 'calculated' || newInputType === 'readonly') {
                                      handleUpdateParameter(index, 'isCalculated', true);
                                    } else {
                                      handleUpdateParameter(index, 'isCalculated', false);
                                      handleUpdateParameter(index, 'formula', '');
                                      handleUpdateParameter(index, 'dependsOn', []);
                                    }
                                  }}
                                  fullWidth
                                  size="medium"
                                  sx={styles.selectField}
                                >
                                  {INPUT_TYPE_OPTIONS.map((option) => (
                                    <MenuItem key={option._id} value={option._id}>
                                      {option.name}
                                    </MenuItem>
                                  ))}
                                </MuiTextField>
                              </Grid>

                              <Grid item xs={2}>
                                <Typography sx={styles.formLabel}>Default Result</Typography>
                                <MuiTextField
                                  disabled={!['new', 'edit'].includes(currentAction) && !param.isCalculated}
                                  value={param.defaultResult || ''}
                                  onChange={(e) => handleUpdateParameter(index, 'defaultResult', e.target.value)}
                                  fullWidth
                                  size="medium"
                                  sx={styles.inputField}
                                />
                              </Grid>

                              {testType === 'multi_parameter_nested' && (
                                <Grid item xs={2}>
                                  <Typography sx={styles.formLabel}>Group By</Typography>
                                  <MuiTextField
                                    disabled={!['new', 'edit'].includes(currentAction)}
                                    value={param.groupBy || ''}
                                    onChange={(e) => handleUpdateParameter(index, 'groupBy', e.target.value)}
                                    fullWidth
                                    size="medium"
                                    sx={styles.inputField}
                                  />
                                </Grid>
                              )}

                              <Grid item xs={1} sx={{ display: 'flex', alignItems: 'end' }}>
                                <Button
                                  color="error"
                                  variant="contained"
                                  disabled={!['new', 'edit'].includes(currentAction) ||
                                    (testType === 'single_parameter' && parameters.length === 1) ||
                                    (testType === 'document' && parameters.length === 1)}
                                  onClick={() => handleRemoveParameter(index)}
                                  startIcon={<DeleteIcon />}
                                  sx={styles.actionButton}
                                >
                                  Remove
                                </Button>
                              </Grid>

                              {/* Formula Section - Only show for calculated parameters */}
                              {(param.inputType === 'calculated' || param.isCalculated) && (
                                <>
                                  <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="h6" fontWeight="bold" color="primary" sx={{ fontSize: '1.5rem' }}>
                                      Formula Configuration
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={6}>
                                    <Typography sx={styles.formLabel}>Formula</Typography>
                                    <MuiTextField
                                      disabled={!['new', 'edit'].includes(currentAction)}
                                      name="formula"
                                      value={param.formula || ''}
                                      onChange={(e) => handleUpdateParameter(index, 'formula', e.target.value)}
                                      fullWidth
                                      size="medium"
                                      sx={styles.inputField}
                                      placeholder="e.g., parameter1 + parameter2 * 0.5"
                                      helperText="Use parameter names in formula. Available functions: round(), sum(), avg(), max(), min()"
                                    />
                                  </Grid>

                                  <Grid item xs={3}>
                                    <Typography sx={styles.formLabel}>Calculation Type</Typography>
                                    <MuiTextField
                                      select
                                      disabled={!['new', 'edit'].includes(currentAction)}
                                      name="calculationType"
                                      value={param.calculationType || 'arithmetic'}
                                      onChange={(e) => handleUpdateParameter(index, 'calculationType', e.target.value)}
                                      fullWidth
                                      size="medium"
                                      sx={styles.selectField}
                                    >
                                      <MenuItem value="arithmetic">Arithmetic</MenuItem>
                                      <MenuItem value="conditional">Conditional</MenuItem>
                                      <MenuItem value="javascript">JavaScript</MenuItem>
                                    </MuiTextField>
                                  </Grid>

                                  <Grid item xs={2}>
                                    <Typography sx={styles.formLabel}>Decimal Places</Typography>
                                    <MuiTextField
                                      disabled={!['new', 'edit'].includes(currentAction)}
                                      name="decimalPlaces"
                                      type="number"
                                      value={param.decimalPlaces || 2}
                                      onChange={(e) => handleUpdateParameter(index, 'decimalPlaces', parseInt(e.target.value) || 2)}
                                      fullWidth
                                      size="medium"
                                      sx={styles.inputField}
                                      inputProps={{ min: 0, max: 6 }}
                                    />
                                  </Grid>

                                  <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={param.isVisible !== false}
                                          name="isVisible"
                                          onChange={(e) => handleUpdateParameter(index, 'isVisible', e.target.checked)}
                                          disabled={!['new', 'edit'].includes(currentAction)}
                                          sx={{ '& .MuiSvgIcon-root': { fontSize: '1.8rem' } }}
                                        />
                                      }
                                      label={
                                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                                          Visible
                                        </Typography>
                                      }
                                    />
                                  </Grid>

                                  {/* Dependencies Section */}
                                  <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom sx={{ fontSize: '1.3rem', fontWeight: 600 }}>
                                      Dependencies (Parameters this calculation depends on):
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                      {parameters
                                        .filter(p => !p.isCalculated && p.tempId !== param.tempId)
                                        .map(depParam => (
                                          <Chip
                                            key={depParam.tempId}
                                            label={depParam.label || depParam.name}
                                            variant={param.dependsOn?.includes(depParam.name) ? "filled" : "outlined"}
                                            color={param.dependsOn?.includes(depParam.name) ? "primary" : "default"}
                                            onClick={() => {
                                              const currentDependsOn = param.dependsOn || [];
                                              const newDependsOn = currentDependsOn.includes(depParam.name)
                                                ? currentDependsOn.filter(d => d !== depParam.name)
                                                : [...currentDependsOn, depParam.name];
                                              handleUpdateParameter(index, 'dependsOn', newDependsOn);
                                            }}
                                            disabled={!['new', 'edit'].includes(currentAction)}
                                            sx={styles.chip}
                                          />
                                        ))}
                                    </Stack>
                                    {param.dependsOn && param.dependsOn.length > 0 && (
                                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', mt: 1 }}>
                                        Depends on: {param.dependsOn.join(', ')}
                                      </Typography>
                                    )}
                                  </Grid>
                                </>
                              )}
                            </Grid>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </CardContent>
                </Card>

                {/* Additional Information Section */}
                <Card sx={styles.sectionCard}>
                  <CardHeader
                    title="Additional Information"
                    titleTypographyProps={{
                      variant: 'h4',
                      fontWeight: 700,
                      fontSize: '1.8rem',
                      color: '#2c3e50'
                    }}
                    sx={{
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: '1.6rem' }}>
                          Interpretation
                        </Typography>
                        <Box sx={{ border: '2px solid #e0e0e0', borderRadius: 2 }}>
                          <CKEditor
                            editor={ClassicEditor}
                            config={editorConfiguration}
                            data={interpretation}
                            disabled={!['new', 'edit'].includes(currentAction)}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setInterpretation(data);
                            }}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: '1.6rem' }}>
                          Notes
                        </Typography>
                        <Box sx={{ border: '2px solid #e0e0e0', borderRadius: 2 }}>
                          <CKEditor
                            editor={ClassicEditor}
                            config={editorConfiguration}
                            data={notes}
                            disabled={!['new', 'edit'].includes(currentAction)}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setNotes(data);
                            }}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: '1.6rem' }}>
                          Comments
                        </Typography>
                        <Box sx={{ border: '2px solid #e0e0e0', borderRadius: 2 }}>
                          <CKEditor
                            editor={ClassicEditor}
                            config={editorConfiguration}
                            data={comments}
                            disabled={!['new', 'edit'].includes(currentAction)}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setComments(data);
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </FormProvider>
        </Container>
      </Paper>

      {/* Dialogs */}
      <Dialog open={helpdescriptiondialog} onClose={() => { setHelpdescriptiondialog(false); setSelectedParameter(null); }} scroll="body" maxWidth="xl">
        <LisTestItemMasterHelpForm
          onClose={() => { setHelpdescriptiondialog(false); setSelectedParameter(null); }}
          onAdd={handleAddHelpDescription}
          parameters={parameters}
          existingHelpDescriptions={parameters.find(p =>
            p._id === selectedParameter || p.tempId === selectedParameter
          )?.helpDescriptions || ''}
        />
      </Dialog>

      <Dialog open={refrencedialog} onClose={() => { setRefrencedialog(false); setSelectedParameter(null); }} scroll="body" maxWidth="xl">
        <LisTestItemRefrenceRangeForm
          onClose={() => { setRefrencedialog(false); setSelectedParameter(null); }}
          onAdd={handleAddRefrenceRange}
          parameters={parameters}
          existingReferenceRanges={refrencelist}
        />
      </Dialog>

      <Dialog open={refrencTextedialog} onClose={() => { setRefrencTextedialog(false); setSelectedParameter(null); }} scroll="body" maxWidth="xl">
        <LisTestItemRefrenceTextRangeForm
          onClose={() => { setRefrencTextedialog(false); setSelectedParameter(null); }}
          onAdd={handleAddReferenceRangeText}
          parameters={parameters}
          existingReferenceRangeText={parameters.find(p =>
            p._id === selectedParameter || p.tempId === selectedParameter
          )?.referenceRangeText || ''}
        />
      </Dialog>

      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '90%', borderRadius: 3, overflow: 'hidden' } }}
        maxWidth="xl"
        scroll="body"
      >
        <Box sx={styles.header}>
          <Stack
            padding={2}
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '2rem' }}>
              Lab Test Master Query Window
            </Typography>
            <IconButton onClick={() => setOpenQueryDialog(false)} sx={{ color: 'white' }}>
              <CloseIcon fontSize="large" />
            </IconButton>
          </Stack>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <MuiTextField
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1.3rem',
                  padding: '0'
                },
                '& .MuiOutlinedInput-input': {
                  padding: '12px 16px',
                  fontSize: '1.3rem'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1.3rem'
                }
              }}
              label="Search Test Names"
              variant="outlined"
              value={input || ''}
              onChange={handleChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Stack>
          <Table
            columns={TEST_ITEM_HEADER}
            data={(testitemdataObj && testitemdataObj.data) || []}
            loading={testitemdataObj.loading}
            onRowClick={(row) => {
              setSelectedRecord(row);
              setOpenQueryDialog(false);
              setCurrentAction(); // Reset action when selecting from query
            }}
          />
        </CardContent>
      </Dialog>
    </>
  );
};